import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/feature/AppLayout";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/feature/Breadcrumb";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/feature/EmptyState";
import FilterPanel, { type FilterValue } from "@/components/feature/FilterPanel";
import FilterDrawer from "@/components/feature/FilterDrawer";
import { animeList } from "@/mocks/anime";
import { categories } from "@/mocks/categories";
import { sortOptions, type SortValue } from "@/mocks/filters";
import { PAGE_SIZE, sortAnime } from "@/utils/anime";
import { usePageMeta } from "@/hooks/usePageMeta";

const STEP = 8;

export default function ListPage() {
  usePageMeta(
    "动漫筛选 - 全部动漫在线浏览 - 动漫免费在观看",
    "在动漫免费在观看按题材、地区、年份与热门程度筛选动漫作品，快速找到想看的科幻、奇幻、恋爱、热血等题材动漫。",
    "动漫筛选,动漫列表,动漫在线观看,免费观看动漫,动漫分类,热门动漫",
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const filter: FilterValue = {
    genre: searchParams.get("genre") || "",
    region: searchParams.get("region") || "",
    year: searchParams.get("year") || "",
    sort: (searchParams.get("sort") as SortValue) || "popular",
  };

  const [draft, setDraft] = useState<FilterValue>(filter);
  const [visibleCount, setVisibleCount] = useState(STEP);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    setVisibleCount(STEP);
  }, [searchParams]);

  const filtered = useMemo(() => {
    const list = animeList.filter((anime) => {
      if (filter.genre && !anime.categories.includes(filter.genre)) return false;
      if (filter.region && anime.region !== filter.region) return false;
      if (filter.year && String(anime.year) !== filter.year) return false;
      return true;
    });
    return sortAnime(list, filter.sort);
  }, [filter.genre, filter.region, filter.year, filter.sort]);

  const visible = filtered.slice(0, visibleCount);
  const activeCount = [filter.genre, filter.region, filter.year].filter(Boolean).length;

  const applyFilter = (next: FilterValue) => {
    const params = new URLSearchParams();
    if (next.genre) params.set("genre", next.genre);
    if (next.region) params.set("region", next.region);
    if (next.year) params.set("year", next.year);
    if (next.sort) params.set("sort", next.sort);
    setSearchParams(params, { replace: true });
    setDrawerOpen(false);
  };

  const resetFilter = () => {
    setSearchParams(new URLSearchParams(), { replace: true });
  };

  return (
    <AppLayout>
      <div className="px-4 md:px-6 pt-5">
        <Breadcrumb items={[{ label: "全部动漫筛选" }]} />

        <header className="mt-3 flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">全部动漫 · 综合筛选</h1>
            <p className="mt-1 text-[12.5px] text-foreground-600">
              共 <strong className="text-accent-600">{filtered.length}</strong> 部作品符合当前条件，已按
              {sortOptions.find((item) => item.value === filter.sort)?.label} 排序
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setDrawerOpen(true)}
              className="lg:hidden h-9 px-3 rounded-md bg-primary-500 text-background-50 text-[12px] flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-filter-3-line" />
              筛选条件
              {activeCount ? <span className="text-[10px]">({activeCount})</span> : null}
            </button>
            {activeCount || filter.sort !== "popular" ? (
              <button
                type="button"
                onClick={resetFilter}
                className="h-9 px-3 rounded-md border border-background-300 text-[12px] text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1"
              >
                <i className="ri-close-line" />
                清空条件
              </button>
            ) : null}
          </div>
        </header>

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-[250px_1fr] gap-6">
          <aside className="hidden lg:block">
            <div className="sticky top-6">
              <FilterPanel
                value={filter}
                onChange={applyFilter}
                layout="sidebar"
                genreOptions={categories.map((cat) => ({ label: cat.name, value: cat.name }))}
              />
              <div className="mt-5 rounded-lg bg-primary-950 p-4 text-background-200">
                <p className="font-heading text-[13px] font-semibold text-background-50">筛选小技巧</p>
                <p className="mt-1.5 text-[11px] leading-relaxed">
                  先选题材再看地区，能更快缩小范围。想找新番就按「最近更新」排序，想找口碑作就按「评分最高」。
                </p>
              </div>
            </div>
          </aside>

          <section>
            {visible.length ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-x-3 gap-y-5">
                  {visible.map((anime) => (
                    <AnimeCard key={anime.id} anime={anime} />
                  ))}
                </div>
                <div className="mt-7 flex flex-col items-center gap-2">
                  <p className="text-[12px] text-foreground-500">
                    已显示 {visible.length} / {filtered.length} 部
                  </p>
                  {visible.length < filtered.length ? (
                    <button
                      type="button"
                      onClick={() => setVisibleCount((prev) => prev + STEP)}
                      className="h-10 px-6 rounded-md bg-primary-500 text-background-50 text-[13px] hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
                    >
                      <i className="ri-add-line" />
                      加载更多动漫
                    </button>
                  ) : (
                    <span className="text-[12px] text-foreground-400 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line" />
                      已经到底啦，去看看其他题材吧
                    </span>
                  )}
                </div>
              </>
            ) : (
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有符合条件的动漫"
                description="当前筛选条件比较严格，建议减少一个条件再试试。也可以直接浏览全部作品。"
                onReset={resetFilter}
                resetLabel="清除全部筛选"
              >
                <div className="mt-4 flex flex-wrap justify-center gap-1.5">
                  {categories.slice(0, 4).map((cat) => (
                    <Link
                      key={cat.id}
                      to={`/category/${cat.id}`}
                      className="px-2.5 py-1 rounded-full bg-background-200 text-[11px] text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {cat.name}动漫
                    </Link>
                  ))}
                </div>
              </EmptyState>
            )}
          </section>
        </div>
      </div>

      <FilterDrawer
        open={drawerOpen}
        value={draft}
        onChange={setDraft}
        onClose={() => setDrawerOpen(false)}
        onApply={applyFilter}
        onReset={() => {
          setDraft({ ...filter, genre: "", region: "", year: "" });
        }}
        showGenre
      />
      <SiteFooter />
    </AppLayout>
  );
}